/*=========================================================================================
  File Name: dialog.js
  Description: Shared modal-dialog behaviour - Escape to close, focus trap, focus restore.

               Addresses WCAG 2.2 SC 2.1.1 Keyboard (a modal you can open but not leave
               with the keyboard is a trap) and SC 4.1.2 Name, Role, Value, which is what
               the audit raised as "Dialog box not marked up" (34.1 - 34.3).

  Usage:
    import dialogMixin from '@/mixins/dialog';
    export default {
      mixins: [dialogMixin],
      methods: {
        // Required. Called on Escape and by the dialog's own close control.
        onDialogClose() { ... },
      },
    };

  The component's dialog root must carry ref="dialog", plus role="dialog",
  aria-modal="true" and an accessible name (aria-label or aria-labelledby).
==========================================================================================*/

// Elements that can hold keyboard focus. `:not([tabindex='-1'])` keeps programmatically
// focusable-but-not-tabbable nodes out of the tab cycle.
export const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'video[controls]',
  'audio[controls]',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const dialogMixin = {
  data() {
    return {
      // The element focus should return to once the dialog closes.
      dialogReturnFocusTo: null,
    };
  },

  mounted() {
    this.dialogReturnFocusTo = document.activeElement;
    document.addEventListener('keydown', this.handleDialogKeydown, true);
    this.$nextTick(this.focusFirstInDialog);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleDialogKeydown, true);
    this.restoreFocusAfterDialog();
  },

  methods: {
    // Overridden by the component. Named rather than emitted so that Escape and the
    // close button always run identical teardown.
    onDialogClose() {},

    dialogRoot() {
      const root = this.$refs.dialog;
      return root && root.$el ? root.$el : root;
    },

    focusableInDialog() {
      const root = this.dialogRoot();
      if (!root) return [];
      return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) =>
          // The sentinels are part of the trap, not content to cycle through.
          !el.hasAttribute('data-focus-sentinel') &&
          // offsetParent is null for display:none; position:fixed needs the extra check.
          (el.offsetParent !== null || getComputedStyle(el).position === 'fixed'),
      );
    },

    focusFirstInDialog() {
      const root = this.dialogRoot();
      if (!root) return;
      const targets = this.focusableInDialog();
      if (targets.length) {
        targets[0].focus();
        return;
      }
      // Nothing focusable inside (an informational dialog): focus the dialog itself so
      // the screen reader announces it and Escape still reaches us.
      root.setAttribute('tabindex', '-1');
      root.focus();
    },

    focusLastInDialog() {
      const targets = this.focusableInDialog();
      if (targets.length) targets[targets.length - 1].focus();
    },

    restoreFocusAfterDialog() {
      const el = this.dialogReturnFocusTo;
      if (el && typeof el.focus === 'function' && document.contains(el)) {
        el.focus();
      }
      this.dialogReturnFocusTo = null;
    },

    // The keydown trap below cannot see Tab presses that happen while focus is inside a
    // cross-origin <iframe> - those events go to the embedded document. The sentinels in
    // each template catch that case: focus lands on one, and these handlers bounce it back
    // into the dialog. Keydown handles everything else, including Escape.
    handleDialogKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        this.onDialogClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const targets = this.focusableInDialog();
      if (!targets.length) {
        // Keep focus on the dialog rather than letting it escape to the page behind.
        event.preventDefault();
        return;
      }

      const first = targets[0];
      const last = targets[targets.length - 1];
      const active = document.activeElement;
      const root = this.dialogRoot();

      // Focus currently outside the dialog (or on the dialog itself): pull it back in.
      if (!root || !root.contains(active) || active === root) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};

export default dialogMixin;
